import React from 'react';
import { BodyText, Link } from '@gaiads/telia-react-component-library';
import { TFunction } from 'react-i18next';
import {
  AdminBundleItem,
  AdminProductVariantProductTypeEnum,
  BundlePrice,
  Country,
  SalesChannel,
} from '@cdaas/api-clients/dist/product-service-openapi';

import { VariantEverything } from '../utils';

import './utils.css';

export interface VariantEverythingWithAutomaticPricingErrors extends VariantEverything {
  TFIDMissingInCountries?: Country[];
  TFAPMissingInCountries: Country[];
}

export interface AutomaticPricingErrors {
  variantsWithAutomaticPricingErrors: VariantEverythingWithAutomaticPricingErrors[];
  mainItemErroneousSalesChannels: SalesChannel[];
  addonItemsErroneousSalesChannels: SalesChannel[];
}

/**
 * If bundle is sold in some sales channel(s) using new TF (Telia Finance) based automatic pricing,
 * this function checks if any variants in the bundle are missing TFAPs or TFIDs for the countries
 * of the sales channels. For each variant a list of countries where the variant is missing TFAPs or
 * TFIDs is returned. In addition, both for main item and addons of the bundle, a list of sales channels
 * is returned where the bundle can't be sold due to missing TFAPs or TFIDs in the variants.
 *
 * NOTE UPDATE: at least currently only variants used in the main item of a bundle need to have TFIDs.
 * Variants used in addons of the bundle do not need TFIDs.
 */
export const getAutomaticPricingErrors = (
  bundlePrices: BundlePrice[],
  mainItemOptionIds: number[] | undefined,
  items: AdminBundleItem[] | undefined,
  variants: VariantEverything[],
  salesChannelsInSystem: SalesChannel[],
  countriesInSystem: Country[],
): AutomaticPricingErrors => {
  const getSalesChannelsInCountryUsingAutomaticPricing = (countryId: number) => {
    return salesChannelsInSystem.filter(sc => sc.use_automatic_lease_price && sc.country_id === countryId);
  };

  const getBundleSoldInSalesChannelsUsingAutomaticPricing = (countryId: number) => {
    return getSalesChannelsInCountryUsingAutomaticPricing(countryId).filter(sc => {
      return sc.uuid && bundlePrices.map(price => price.sales_channel_uuid).includes(sc.uuid);
    });
  };

  const countriesInWhichBundleIsSoldWitAutomaticPricing = countriesInSystem.filter(country => {
    return !!getBundleSoldInSalesChannelsUsingAutomaticPricing(country.id).length;
  });

  const salesChannelsInWhichBundleIsSoldWithAutomaticPricing = countriesInWhichBundleIsSoldWitAutomaticPricing.flatMap(country => {
    return getBundleSoldInSalesChannelsUsingAutomaticPricing(country.id);
  });

  const variantsWithAutomaticPricingErrors = variants.map(variant => {
    const missingTFAPsInCountries = countriesInWhichBundleIsSoldWitAutomaticPricing.filter(country => {
      return !variant.country_acquisition_prices.find(tfap => tfap.country_id === country.id);
    });
    // TFIDs only required for variants used in main item of bundle
    const missingTFIDsInCountries = mainItemOptionIds?.includes(variant.id)
      ? countriesInWhichBundleIsSoldWitAutomaticPricing.filter(country => {
          return (
            variant.product_type !== AdminProductVariantProductTypeEnum.Service &&
            !variant.finance_product_ids.find(tfid => tfid.country_id === country.id)
          );
        })
      : undefined;
    return {
      ...variant,
      TFAPMissingInCountries: missingTFAPsInCountries,
      TFIDMissingInCountries: missingTFIDsInCountries,
    };
  });

  const getErroneusSalesChannelsForVariants = (
    variants: VariantEverythingWithAutomaticPricingErrors[],
    mainItemVariants: boolean,
  ): SalesChannel[] => {
    const erroneousCountriesForVariants = countriesInWhichBundleIsSoldWitAutomaticPricing.filter(country => {
      return variants.find(variant => {
        const missingTFAP = variant.TFAPMissingInCountries.find(countryMissingTFAP => countryMissingTFAP.id === country.id);
        // TFIDs only required for variants used in main item of bundle
        const missingTFID =
          mainItemVariants && (variant.TFIDMissingInCountries || []).find(countryMissingTFID => countryMissingTFID.id === country.id);
        return missingTFAP || missingTFID;
      });
    });
    return salesChannelsInWhichBundleIsSoldWithAutomaticPricing.filter(salesChannel => {
      return erroneousCountriesForVariants.map(country => country.id).includes(salesChannel.country_id);
    });
  };

  // Check in which sales channels bundle can't be sold because of missing TFAPs or TFIDs in variants used in bundle's main item
  const variantsUsedInMainItem = variantsWithAutomaticPricingErrors.filter(variant => mainItemOptionIds?.includes(variant.id));
  const mainItemErroneousSalesChannels = getErroneusSalesChannelsForVariants(variantsUsedInMainItem, true);

  // Check in which sales channels bundle can't be sold because of missing TFAPs in variants used in bundle's addon items
  const variantsUsedInAddonItems = variantsWithAutomaticPricingErrors.filter(variant => {
    return (items || []).flatMap(item => item.options).includes(variant.id);
  });
  const addonItemsErroneousSalesChannels = getErroneusSalesChannelsForVariants(variantsUsedInAddonItems, false);

  return {
    variantsWithAutomaticPricingErrors,
    mainItemErroneousSalesChannels,
    addonItemsErroneousSalesChannels,
  };
};

export const formulateSingleVariantErrorMessage = (
  t: TFunction<'bundlescreen', undefined>,
  variant: VariantEverythingWithAutomaticPricingErrors,
  mainItemVariant: boolean,
): { title: string; message: JSX.Element } | undefined => {
  // Both variants used in main item and addons need TFAPs
  if (!variant.TFAPMissingInCountries.length) {
    // TFIDs only required for variants used in main item of bundle
    if (!mainItemVariant || !variant?.TFIDMissingInCountries?.length) {
      return undefined;
    }
  }

  const title = variant.TFAPMissingInCountries.length
    ? t('product_variants_section.tf_automatic_pricing_errors.tfap_missing_title')
    : t('product_variants_section.tf_automatic_pricing_errors.tfid_missing_title');

  const messageStart = t('product_variants_section.tf_automatic_pricing_errors.tfap_or_tfid_missing_message_start');
  const messageProductLink = (
    <Link color="black" className="bundle-tfap-or-tfid-missing-product-link" to={`/product/${variant.product_id}/main/`}>
      {t('product_variants_section.tf_automatic_pricing_errors.tfap_or_tfid_missing_link')}
    </Link>
  );
  const messageEnd = variant.TFAPMissingInCountries.length
    ? t('product_variants_section.tf_automatic_pricing_errors.tfap_missing_message_end', {
        countries: variant.TFAPMissingInCountries.map(country => country.name),
      })
    : t('product_variants_section.tf_automatic_pricing_errors.tfid_missing_message_end', {
        countries: (variant.TFIDMissingInCountries || []).map(country => country.name),
      });

  const message = (
    <BodyText>
      {messageStart} {messageProductLink} {messageEnd}
    </BodyText>
  );

  return { title, message };
};

export const formulateBundleMainItemOrAddonsSummaryErrorMessage = (
  t: TFunction<'bundlescreen', undefined>,
  erroneusSalesChannels: SalesChannel[],
): { title: string; message: JSX.Element } | undefined => {
  if (!erroneusSalesChannels.length) {
    return undefined;
  }

  const message = (
    <>
      <BodyText>{t('product_variants_section.tf_automatic_pricing_errors.hidden_from_sales_channels_text_1')}</BodyText>
      <BodyText>
        {`${t('product_variants_section.tf_automatic_pricing_errors.hidden_from_sales_channels_text_2')} `}
        <span style={{ fontWeight: 'bold' }}>{erroneusSalesChannels.map(salesChannel => salesChannel.name).join(', ')}</span>.
      </BodyText>
      <BodyText>{t('product_variants_section.tf_automatic_pricing_errors.hidden_from_sales_channels_text_3')}</BodyText>
    </>
  );

  return {
    title: t('product_variants_section.tf_automatic_pricing_errors.hidden_from_sales_channels_title'),
    message,
  };
};
